import { MessageBox, Notification } from "element-ui";
import NProgress from "nprogress";
import VueRouter from "vue-router";

/**
 * 发版兜底。
 * 商户/运营的习惯是后台标签页一挂几天不刷新，前端每次发版的产物目录都会换名(js.<版本>/)，
 * 旧标签页再去懒加载自己的 chunk 就 404，表现为“界面还在但点什么都没反应”；
 * 若服务器上旧版本目录被清理，连刷新前的最后一个页面也救不回来。
 * 这里做两件事：
 * 1) 捕获 chunk/CSS 加载失败，弹窗请商户主动刷新（不静默 reload，避免提现/补扣款等长表单被清空）；
 * 2) 轮询 version.json，发现线上版本比当前运行的新就软提示，让商户在方便的时候刷新。
 */

// 当前运行代码的版本，由 vue.config.js 注入；本地 serve 时为空串，下面的心跳整体跳过
const CURRENT = process.env.VUE_APP_VERSION || "";
const RELOAD_AT_KEY = "app-update-reload-at";
// 刷新后 60 秒内仍然失败，说明入口 HTML 还是旧的，再 reload 只会死循环
const RELOAD_GAP = 60 * 1000;
const CHECK_GAP = 5 * 60 * 1000;
const VERSION_URL = `${process.env.BASE_URL || "/"}version.json`;
// Loading chunk 404 时，nginx 回退 index.html 会让 JS 解析成 “Unexpected token '<'”
const CHUNK_ERROR_RE = /Loading (CSS )?chunk [\s\S]*failed|ChunkLoadError|Unexpected token '<'/i;

let prompting = false;
let notified = false;

const isChunkError = (err: any): boolean =>
    CHUNK_ERROR_RE.test(String((err && err.message) || err || ""));

// 距上次由本文件触发的刷新是否还很近
const reloadedRecently = (): boolean =>
    Date.now() - Number(sessionStorage.getItem(RELOAD_AT_KEY) || 0) < RELOAD_GAP;

const reload = (): void => {
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
    location.reload();
};

function askReload(): void {
    if (prompting) return;
    prompting = true;
    if (reloadedRecently()) {
        MessageBox.alert(
            "刷新后仍没加载到新版界面，请按 Ctrl+F5 强制刷新，或清除浏览器缓存后重试。",
            "界面需要更新",
            { type: "warning", confirmButtonText: "知道了" }
        ).catch(() => undefined);
        prompting = false;
        return;
    }
    MessageBox.confirm(
        "界面已发布新版本，当前页面缺少所需的文件。刷新后即可继续使用，页面里没保存的表单需要重填。",
        "界面需要更新",
        { confirmButtonText: "立即刷新", cancelButtonText: "稍后", type: "warning" }
    ).then(reload).catch(() => {
        // 选择“稍后”时留在旧页面，下次点击仍会再提示
        prompting = false;
    });
}

/** version.json 与构建产物一起产出，必须在 nginx/CDN 上设为不缓存 */
function checkVersion(): void {
    if (!CURRENT) return;
    fetch(`${VERSION_URL}?_t=${Date.now()}`, { cache: "no-store" })
        .then(res => (res.ok ? res.json() : null))
        .then(data => {
            const latest = data && String(data.version || "");
            if (!latest || latest === CURRENT || notified) return;
            notified = true;
            Notification({
                title: "有新版本界面",
                message: reloadedRecently()
                    ? "刷新后拿到的仍是旧界面，请按 Ctrl+F5 强制刷新。"
                    : "有空时刷新页面即可生效；不会自动刷新，正在填写的内容不受影响。",
                type: "warning",
                duration: 0,
                onClick: () => { if (!reloadedRecently()) reload(); }
            });
        })
        .catch(() => undefined); // 文件未部署或网络异常时不打扰用户
}

export function setupAppUpdate(router: VueRouter): void {
    router.onError((err: any) => {
        // 加载失败不会触发 afterEach，进度条否则会卡在顶部，看起来更像“点不动”
        NProgress.done();
        if (isChunkError(err)) askReload();
    });

    // 按钮点击时才加载的动态组件(弹窗等)失败不会走 router.onError，只会成为未处理的 rejection
    window.addEventListener("unhandledrejection", (event: any) => {
        if (!isChunkError(event.reason)) return;
        event.preventDefault();
        askReload();
    });

    checkVersion();
    window.setInterval(checkVersion, CHECK_GAP);
    document.addEventListener("visibilitychange", () => {
        if (!document.hidden) checkVersion();
    });
}
